import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs, type Appointment, type AppointmentStatus, type ClientDetail, type Consultant, type Client, type Lookup, type Paged, type ServiceModality } from '../api';
import { useMe } from '../auth';
import { Dialog, Empty, ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { ProjectPicker } from '../components/ProjectPicker';
import { PrepareMessageButton, RelatedMessages } from './Mensagens';
import {
  addDays,
  APPOINTMENT_STATUS_LABEL,
  date,
  hm,
  localParts,
  longDate,
  monthLabel,
  SERVICE_MODALITY_LABEL,
  timeOf,
  todayIso,
  weekdayShort,
  zonedIso,
} from '../format';

type View = 'dia' | 'semana' | 'mes' | 'lista';

function rangeFor(view: View, anchor: string): { from: string; to: string; days: string[] } {
  if (view === 'dia') return { from: anchor, to: addDays(anchor, 1), days: [anchor] };
  if (view === 'semana') {
    const dow = (new Date(`${anchor}T12:00:00Z`).getUTCDay() + 6) % 7; // segunda = 0
    const start = addDays(anchor, -dow);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return { from: start, to: addDays(start, 7), days };
  }
  if (view === 'mes') {
    const first = `${anchor.slice(0, 7)}-01`;
    const dow = (new Date(`${first}T12:00:00Z`).getUTCDay() + 6) % 7;
    const start = addDays(first, -dow);
    const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return { from: start, to: addDays(start, 42), days };
  }
  const days = Array.from({ length: 30 }, (_, i) => addDays(anchor, i));
  return { from: anchor, to: addDays(anchor, 30), days };
}

const STATUS_CLASS: Record<AppointmentStatus, string> = {
  agendado: 'badge-brand',
  confirmado: 'badge-ok',
  realizado: 'badge-ok',
  cancelado: 'badge-off',
  reagendado: 'badge-off',
  nao_compareceu: 'badge-off',
};

export function StatusBadge({ status }: { status: AppointmentStatus }) {
  return <span className={`badge ${STATUS_CLASS[status]}`}>{APPOINTMENT_STATUS_LABEL[status]}</span>;
}

export function AppointmentItem({ a, compact = false }: { a: Appointment; compact?: boolean }) {
  const struck = a.status === 'cancelado' || a.status === 'reagendado';
  const body = (
    <>
      <div className="title" style={struck ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}>
        <span className="muted small">
          {timeOf(a.startsAt)}–{timeOf(a.endsAt)}
        </span>{' '}
        {a.title} {!compact && <StatusBadge status={a.status} />}
      </div>
      {!compact && !a.private && (
        <div className="meta">
          {a.clientName && <span>{a.clientName}</span>}
          {a.projectName && <span>{a.projectName}</span>}
          {a.modality && <span>{SERVICE_MODALITY_LABEL[a.modality]}</span>}
          <span>{a.participants.map((p) => p.name).join(', ')}</span>
        </div>
      )}
      {!compact && a.private && <div className="meta">{a.participants.map((p) => p.name).join(', ')} · horário bloqueado</div>}
    </>
  );
  if (a.private) return <div className="list-item">{body}</div>;
  return (
    <Link className="list-item" to={`/agenda/${a.id}`}>
      {body}
    </Link>
  );
}

export function AgendaPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches;
  const view = (params.get('v') as View) || (isMobile ? 'lista' : 'semana');
  const anchor = params.get('d') || todayIso();
  const [filters, setFilters] = useState({ scope: 'mine', consultantId: '', clientId: '', projectId: '', modality: '', status: '' });
  const set = (patch: Record<string, string>) => setParams((p) => ({ ...Object.fromEntries(p), ...patch }));
  const range = rangeFor(view, anchor);

  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const clients = useQuery({ queryKey: ['clients', 'active-min'], queryFn: () => api.get<Paged<Client>>('/clients?active=true&pageSize=200') });
  const q = useQuery({
    queryKey: ['appointments', range.from, range.to, filters],
    queryFn: () =>
      api.get<{ data: Appointment[] }>(
        `/appointments${qs({
          from: zonedIso(range.from, '00:00'),
          to: zonedIso(range.to, '00:00'),
          mine: filters.scope === 'mine' ? 'true' : undefined,
          consultantId: filters.scope === 'consultant' ? filters.consultantId : undefined,
          clientId: filters.clientId,
          projectId: filters.projectId,
          modality: filters.modality,
          status: filters.status,
        })}`,
      ),
    placeholderData: (prev) => prev,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of q.data?.data ?? []) {
      const d = localParts(a.startsAt).date;
      map.set(d, [...(map.get(d) ?? []), a]);
    }
    return map;
  }, [q.data]);

  const step = view === 'dia' ? 1 : view === 'semana' ? 7 : view === 'mes' ? 0 : 30;
  const move = (dir: 1 | -1) => {
    if (view === 'mes') {
      const [y, m] = anchor.split('-').map(Number) as [number, number];
      const d = new Date(Date.UTC(y, m - 1 + dir, 1));
      set({ d: d.toISOString().slice(0, 10) });
    } else set({ d: addDays(anchor, dir * step) });
  };
  const title =
    view === 'dia' ? longDate(anchor) : view === 'mes' ? monthLabel(anchor) : `${date(range.from)} a ${date(addDays(range.to, -1))}`;
  const today = todayIso();

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Agenda</h1>
          <p>{title}</p>
        </div>
        <div className="actions">
          <Link className="btn btn-primary" to={`/agenda/novo?d=${view === 'dia' ? anchor : today}`}>
            Novo compromisso
          </Link>
        </div>
      </div>

      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Visualização">
          {(['dia', 'semana', 'mes', 'lista'] as View[]).map((v) => (
            <button key={v} aria-pressed={view === v} onClick={() => set({ v })}>
              {{ dia: 'Dia', semana: 'Semana', mes: 'Mês', lista: 'Lista' }[v]}
            </button>
          ))}
        </div>
        <div className="actions">
          <button className="btn btn-sm" onClick={() => move(-1)} aria-label="Período anterior">
            ‹
          </button>
          <button className="btn btn-sm" onClick={() => set({ d: today })}>
            Hoje
          </button>
          <button className="btn btn-sm" onClick={() => move(1)} aria-label="Próximo período">
            ›
          </button>
        </div>
      </div>
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="ag-scope">Consultor</label>
        <select
          id="ag-scope"
          value={filters.scope === 'consultant' ? filters.consultantId : filters.scope}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => (v === 'mine' || v === 'all' ? { ...f, scope: v, consultantId: '' } : { ...f, scope: 'consultant', consultantId: v }));
          }}
        >
          <option value="mine">Minha agenda</option>
          <option value="all">Todos os consultores</option>
          {consultants.data?.data
            .filter((c) => c.active && c.id !== me.consultantId)
            .map((c) => (
              <option key={c.id} value={c.id}>
                Agenda de {c.name}
              </option>
            ))}
        </select>
        <label className="visually-hidden" htmlFor="ag-client">Cliente</label>
        <select id="ag-client" value={filters.clientId} onChange={(e) => setFilters((f) => ({ ...f, clientId: e.target.value }))}>
          <option value="">Todos os clientes</option>
          {clients.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tradeName || c.name}
            </option>
          ))}
        </select>
        <label className="visually-hidden" htmlFor="ag-mod">Modalidade</label>
        <select id="ag-mod" value={filters.modality} onChange={(e) => setFilters((f) => ({ ...f, modality: e.target.value }))} style={{ flex: '0 1 160px' }}>
          <option value="">Remoto e presencial</option>
          <option value="remoto">Remoto</option>
          <option value="presencial">Presencial</option>
        </select>
        <label className="visually-hidden" htmlFor="ag-status">Situação</label>
        <select id="ag-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} style={{ flex: '0 1 180px' }}>
          <option value="">Todas as situações</option>
          {Object.entries(APPOINTMENT_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : view === 'mes' ? (
        <div className="month-grid" role="grid" aria-label={monthLabel(anchor)}>
          {range.days.slice(0, 7).map((d) => (
            <div key={`h${d}`} className="month-head" role="columnheader">
              {weekdayShort(d)}
            </div>
          ))}
          {range.days.map((d) => {
            const items = byDay.get(d) ?? [];
            const outside = d.slice(0, 7) !== anchor.slice(0, 7);
            return (
              <button
                key={d}
                role="gridcell"
                className={`month-cell${outside ? ' outside' : ''}${d === today ? ' today' : ''}`}
                onClick={() => set({ v: 'dia', d })}
                aria-label={`${date(d)}: ${items.length} compromisso(s)`}
              >
                <span className="month-day">{Number(d.slice(8))}</span>
                {items.slice(0, 3).map((a) => (
                  <span key={a.id} className={`month-event${a.status === 'cancelado' || a.status === 'reagendado' ? ' struck' : ''}`}>
                    {timeOf(a.startsAt)} {a.title}
                  </span>
                ))}
                {items.length > 3 && <span className="month-more">+{items.length - 3}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={view === 'semana' ? 'week-grid' : 'grid'}>
          {range.days
            .filter((d) => view !== 'lista' || (byDay.get(d)?.length ?? 0) > 0)
            .map((d) => (
              <section key={d} className={`day-col${d === today ? ' today' : ''}`}>
                <h2 className="day-title">
                  <button className="btn-link" onClick={() => set({ v: 'dia', d })}>
                    {view === 'semana' ? `${weekdayShort(d)} ${date(d).slice(0, 5)}` : longDate(d)}
                  </button>
                </h2>
                <div className="list">
                  {(byDay.get(d) ?? []).map((a) => (
                    <AppointmentItem key={a.id} a={a} compact={view === 'semana'} />
                  ))}
                  {view !== 'lista' && (byDay.get(d)?.length ?? 0) === 0 && <span className="small muted">Sem compromissos.</span>}
                </div>
              </section>
            ))}
          {view === 'lista' && byDay.size === 0 && <Empty>Nenhum compromisso nos próximos 30 dias com esses filtros.</Empty>}
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ formulário

interface ConflictInfo {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  participants: string[];
}

export function AppointmentFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const existing = useQuery({ queryKey: ['appointment', id], queryFn: () => api.get<Appointment>(`/appointments/${id}`), enabled: editing });
  const types = useQuery({ queryKey: ['appointment-types'], queryFn: () => api.get<{ data: Lookup[] }>('/appointment-types') });
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const clients = useQuery({ queryKey: ['clients', 'active-min'], queryFn: () => api.get<Paged<Client>>('/clients?active=true&pageSize=200') });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.get<{ data: Lookup[] }>('/categories') });
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api.get<{ data: Lookup[] }>('/areas') });

  const [form, setForm] = useState({
    typeId: '',
    title: '',
    projectId: null as string | null,
    clientId: '',
    modality: 'remoto' as ServiceModality,
    day: params.get('d') || todayIso(),
    start: '09:00',
    end: '10:00',
    plannedBreakMinutes: 0,
    plannedCategoryId: '',
    plannedAreaId: '',
    location: '',
    meetingLink: '',
    description: '',
    participantIds: [me.consultantId],
    contactIds: [] as string[],
  });
  const [conflicts, setConflicts] = useState<ConflictInfo[] | null>(null);

  useEffect(() => {
    const a = existing.data;
    if (!a) return;
    const s = localParts(a.startsAt);
    setForm({
      typeId: a.typeId ?? '',
      title: a.title,
      projectId: a.projectId,
      clientId: a.clientId ?? '',
      modality: a.modality ?? 'remoto',
      day: s.date,
      start: s.time,
      end: localParts(a.endsAt).time,
      plannedBreakMinutes: a.plannedBreakMinutes,
      plannedCategoryId: a.plannedCategoryId ?? '',
      plannedAreaId: a.plannedAreaId ?? '',
      location: a.location ?? '',
      meetingLink: a.meetingLink ?? '',
      description: a.description ?? '',
      participantIds: a.participants.map((p) => p.id),
      contactIds: a.contacts.map((c) => c.id),
    });
  }, [existing.data]);
  useEffect(() => {
    if (!form.typeId && types.data) setForm((f) => ({ ...f, typeId: types.data.data.find((t) => t.kind === 'client' && t.active)?.id ?? '' }));
  }, [types.data, form.typeId]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const type = types.data?.data.find((t) => t.id === form.typeId);
  const isPersonal = type?.kind === 'personal';
  const clientDetail = useQuery({
    queryKey: ['client', form.clientId],
    queryFn: () => api.get<ClientDetail>(`/clients/${form.clientId}`),
    enabled: Boolean(form.clientId) && !isPersonal,
  });
  const crossesMidnight = form.end <= form.start;

  const m = useMutation({
    mutationFn: async (confirmConflicts: boolean) => {
      const startsAt = zonedIso(form.day, form.start);
      const endsAt = zonedIso(crossesMidnight ? addDays(form.day, 1) : form.day, form.end);
      const body = {
        typeId: form.typeId,
        title: form.title,
        projectId: isPersonal ? null : form.projectId,
        clientId: isPersonal ? null : form.clientId || null,
        modality: form.modality,
        startsAt,
        endsAt,
        plannedBreakMinutes: form.plannedBreakMinutes,
        plannedCategoryId: form.plannedCategoryId || null,
        plannedAreaId: form.plannedAreaId || null,
        location: form.location,
        meetingLink: form.meetingLink,
        description: form.description,
        participantIds: form.participantIds,
        contactIds: isPersonal ? [] : form.contactIds,
        confirmConflicts,
      };
      if (editing) {
        await api.put(`/appointments/${id}`, { ...body, version: existing.data!.version });
        return id!;
      }
      return (await api.post<{ id: string }>('/appointments', body)).id;
    },
    onSuccess: (aid) => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['appointment', aid] });
      void qc.invalidateQueries({ queryKey: ['me-overview'] });
      navigate(`/agenda/${aid}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'schedule_conflict') setConflicts((e.details as { conflicts: ConflictInfo[] }).conflicts);
    },
  });

  if (editing && existing.isLoading) return <Loading />;
  const err = m.error instanceof ApiError && m.error.code === 'schedule_conflict' ? null : m.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    setConflicts(null);
    m.mutate(false);
  }

  return (
    <>
      <div className="page-head">
        <h1>{editing ? 'Editar compromisso' : 'Novo compromisso'}</h1>
      </div>
      <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 860 }}>
        <ErrorAlert error={err} />
        {conflicts && (
          <div className="alert alert-warning" role="alert">
            <strong>Sobreposição de horário</strong>
            <ul style={{ margin: '0.25rem 0', paddingLeft: '1.2rem' }}>
              {conflicts.map((c) => (
                <li key={c.id}>
                  {timeOf(c.startsAt)}–{timeOf(c.endsAt)} · {c.title} ({c.participants.join(', ')})
                </li>
              ))}
            </ul>
            <div className="actions">
              <button type="button" className="btn btn-sm" onClick={() => m.mutate(true)}>
                A sobreposição é intencional — salvar
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConflicts(null)}>
                Ajustar horário
              </button>
            </div>
          </div>
        )}
        <div className="form-row">
          <Field label="Tipo" required>
            {(p) => (
              <select {...p} value={form.typeId} onChange={(e) => set('typeId', e.target.value)}>
                {types.data?.data
                  .filter((t) => t.active || t.id === form.typeId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            )}
          </Field>
          <Field label="Título" required error={fieldError(err, 'title')}>
            {(p) => <input {...p} value={form.title} onChange={(e) => set('title', e.target.value)} />}
          </Field>
        </div>
        {isPersonal ? (
          <p className="small muted">Compromisso pessoal: os demais sócios veem apenas o horário bloqueado.</p>
        ) : (
          <>
            <Field label="Projeto" hint="Opcional: compromisso avulso pode ser vinculado depois.">
              {(p) => (
                <ProjectPicker
                  id={p.id}
                  value={form.projectId}
                  allowEmpty="Sem projeto (avulso)"
                  onChange={(pid, proj) => setForm((f) => ({ ...f, projectId: pid, clientId: proj?.clientId ?? f.clientId, contactIds: proj && proj.clientId !== f.clientId ? [] : f.contactIds }))}
                />
              )}
            </Field>
            {!form.projectId && (
              <Field label="Cliente" hint="Opcional para atividades internas.">
                {(p) => (
                  <select {...p} value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value, contactIds: [] }))}>
                    <option value="">Sem cliente</option>
                    {clients.data?.data.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.tradeName || c.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
          </>
        )}
        <div className="form-row">
          <Field label="Data" required>
            {(p) => <input {...p} type="date" value={form.day} onChange={(e) => set('day', e.target.value)} />}
          </Field>
          <div className="form-row">
            <Field label="Início" required>
              {(p) => <input {...p} type="time" value={form.start} onChange={(e) => set('start', e.target.value)} />}
            </Field>
            <Field label="Fim" required hint={crossesMidnight ? 'Termina no dia seguinte.' : undefined}>
              {(p) => <input {...p} type="time" value={form.end} onChange={(e) => set('end', e.target.value)} />}
            </Field>
          </div>
        </div>
        <div className="form-row">
          <Field label="Modalidade">
            {(p) => (
              <select {...p} value={form.modality} onChange={(e) => set('modality', e.target.value as ServiceModality)}>
                <option value="remoto">Remoto</option>
                <option value="presencial">Presencial</option>
              </select>
            )}
          </Field>
          <Field label="Intervalo previsto (min)">
            {(p) => <input {...p} type="number" min={0} inputMode="numeric" value={form.plannedBreakMinutes} onChange={(e) => set('plannedBreakMinutes', Number(e.target.value) || 0)} />}
          </Field>
        </div>
        <fieldset>
          <legend>Consultores</legend>
          <div className="checks">
            {consultants.data?.data
              .filter((c) => c.active || form.participantIds.includes(c.id))
              .map((c) => (
                <label key={c.id} className="check">
                  <input
                    type="checkbox"
                    checked={form.participantIds.includes(c.id)}
                    onChange={(e) => set('participantIds', e.target.checked ? [...form.participantIds, c.id] : form.participantIds.filter((x) => x !== c.id))}
                  />
                  {c.name}
                  {c.id === me.consultantId ? ' (você)' : ''}
                </label>
              ))}
          </div>
        </fieldset>
        {!isPersonal && form.clientId && (clientDetail.data?.contacts.length ?? 0) > 0 && (
          <fieldset>
            <legend>Contatos participantes</legend>
            <div className="checks">
              {clientDetail.data!.contacts
                .filter((c) => c.active || form.contactIds.includes(c.id))
                .map((c) => (
                  <label key={c.id} className="check">
                    <input
                      type="checkbox"
                      checked={form.contactIds.includes(c.id)}
                      onChange={(e) => set('contactIds', e.target.checked ? [...form.contactIds, c.id] : form.contactIds.filter((x) => x !== c.id))}
                    />
                    {c.name}
                  </label>
                ))}
            </div>
          </fieldset>
        )}
        {!isPersonal && (
          <div className="form-row">
            <Field label="Categoria prevista">
              {(p) => (
                <select {...p} value={form.plannedCategoryId} onChange={(e) => set('plannedCategoryId', e.target.value)}>
                  <option value="">—</option>
                  {categories.data?.data
                    .filter((c) => c.active || c.id === form.plannedCategoryId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              )}
            </Field>
            <Field label="Módulo/área previsto">
              {(p) => (
                <select {...p} value={form.plannedAreaId} onChange={(e) => set('plannedAreaId', e.target.value)}>
                  <option value="">—</option>
                  {areas.data?.data
                    .filter((c) => c.active || c.id === form.plannedAreaId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              )}
            </Field>
          </div>
        )}
        <div className="form-row">
          <Field label="Local / endereço">{(p) => <input {...p} value={form.location} onChange={(e) => set('location', e.target.value)} />}</Field>
          <Field label="Link da reunião">{(p) => <input {...p} type="url" value={form.meetingLink} onChange={(e) => set('meetingLink', e.target.value)} />}</Field>
        </div>
        <Field label="Descrição">{(p) => <textarea {...p} value={form.description} onChange={(e) => set('description', e.target.value)} />}</Field>
        <p className="small muted">Salvar o compromisso não envia nenhuma mensagem ao cliente.</p>
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !form.title || !form.typeId || form.participantIds.length === 0}>
            {m.isPending ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}

// ------------------------------------------------------------------ detalhe

export function AppointmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reschedule, setReschedule] = useState(false);
  const q = useQuery({ queryKey: ['appointment', id], queryFn: () => api.get<Appointment>(`/appointments/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['appointment', id] });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
    void qc.invalidateQueries({ queryKey: ['me-overview'] });
  };
  const status = useMutation({
    mutationFn: ({ s, notes }: { s: AppointmentStatus; notes?: string }) => api.post(`/appointments/${id}/status`, { status: s, notes, version: q.data!.version }),
    onSuccess: refresh,
  });
  const exec = useMutation({
    mutationFn: () => api.post<{ id: string; existing: boolean }>(`/appointments/${id}/execution`, {}),
    onSuccess: (r) => navigate(`/execucoes/${r.id}`),
  });
  // Simplificação: um clique marca "realizado" e já abre o registro do atendimento preenchido.
  const doneAndRegister = useMutation({
    mutationFn: async () => {
      await api.post(`/appointments/${id}/status`, { status: 'realizado', version: q.data!.version });
      return api.post<{ id: string; existing: boolean }>(`/appointments/${id}/execution`, {});
    },
    onSuccess: (r) => {
      refresh();
      navigate(`/execucoes/${r.id}`);
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const a = q.data!;
  const active = a.status === 'agendado' || a.status === 'confirmado';
  const started = new Date(a.startsAt) <= new Date();
  const ended = new Date(a.endsAt) <= new Date();
  const s = localParts(a.startsAt);

  function outcome(st: AppointmentStatus, ask?: string) {
    const notes = ask ? window.prompt(ask) : undefined;
    if (notes === null) return;
    status.mutate({ s: st, notes: notes || undefined });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to={`/agenda?v=dia&d=${s.date}`}>← Agenda</Link>
          </p>
          <h1>
            {a.title} <StatusBadge status={a.status} />
          </h1>
          <p>
            {longDate(s.date)} · {timeOf(a.startsAt)}–{timeOf(a.endsAt)} (horário de Fortaleza)
          </p>
        </div>
        {active && (
          <div className="actions">
            <Link className="btn" to={`/agenda/${a.id}/editar`}>
              Editar
            </Link>
            <button className="btn" onClick={() => setReschedule(true)}>
              Reagendar
            </button>
          </div>
        )}
      </div>
      <ErrorAlert error={status.error ?? exec.error} />
      {active && ended && (
        <div className="alert alert-warning">
          <strong>Resultado pendente</strong>O horário previsto terminou. Registre se foi realizado, cancelado, reagendado ou se o cliente não compareceu.
        </div>
      )}
      {a.rescheduledToId && (
        <div className="alert alert-info">
          Reagendado. <Link to={`/agenda/${a.rescheduledToId}`}>Ver nova marcação</Link>
        </div>
      )}
      {a.rescheduledFromId && (
        <div className="alert alert-info">
          Esta marcação substitui um compromisso reagendado. <Link to={`/agenda/${a.rescheduledFromId}`}>Ver original</Link>
        </div>
      )}
      <div className="grid grid-2">
        <section className="card">
          <h2>Detalhes</h2>
          <dl className="props">
            <dt>Tipo</dt>
            <dd>{a.typeName}</dd>
            <dt>Cliente</dt>
            <dd>{a.clientName ?? '—'}</dd>
            <dt>Projeto</dt>
            <dd>{a.projectId ? <Link to={`/projetos/${a.projectId}`}>{a.projectName}</Link> : 'Sem projeto (avulso)'}</dd>
            <dt>Consultores</dt>
            <dd>{a.participants.map((p) => p.name).join(', ')}</dd>
            <dt>Contatos</dt>
            <dd>{a.contacts.map((c) => c.name).join(', ') || '—'}</dd>
            <dt>Modalidade</dt>
            <dd>{a.modality ? SERVICE_MODALITY_LABEL[a.modality] : '—'}</dd>
            <dt>Intervalo previsto</dt>
            <dd>{a.plannedBreakMinutes ? hm(a.plannedBreakMinutes) : '—'}</dd>
            <dt>Local / link</dt>
            <dd>
              {a.location || '—'}
              {a.meetingLink && (
                <>
                  {' · '}
                  <a href={a.meetingLink} target="_blank" rel="noopener noreferrer">
                    abrir link
                  </a>
                </>
              )}
            </dd>
            <dt>Descrição</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{a.description || '—'}</dd>
            {a.outcomeNotes && (
              <>
                <dt>Observação do resultado</dt>
                <dd>{a.outcomeNotes}</dd>
              </>
            )}
          </dl>
        </section>
        <section className="card">
          <h2>Ações</h2>
          <p className="small muted">
            {active && started && a.typeKind !== 'personal'
              ? 'Aconteceu? Clique no botão azul: o atendimento abre já preenchido com projeto, data, horário e categoria. Nada é enviado ao cliente.'
              : 'Nenhuma destas ações envia mensagem ao cliente.'}
          </p>
          <ErrorAlert error={doneAndRegister.error} />
          <div className="grid" style={{ gap: '0.5rem' }}>
            {active && started && a.typeKind !== 'personal' && (
              <button className="btn btn-primary" disabled={doneAndRegister.isPending || status.isPending} onClick={() => doneAndRegister.mutate()}>
                Foi realizado — registrar atendimento
              </button>
            )}
            {a.status === 'agendado' && (
              <button className="btn" disabled={status.isPending} onClick={() => outcome('confirmado')}>
                Marcar como confirmado
              </button>
            )}
            {active && started && (
              <button className={a.typeKind === 'personal' ? 'btn btn-primary' : 'btn'} disabled={status.isPending} onClick={() => outcome('realizado')}>
                {a.typeKind === 'personal' ? 'Foi realizado' : 'Só marcar como realizado'}
              </button>
            )}
            {a.status === 'realizado' && a.typeKind !== 'personal' && (
              <button className="btn btn-primary" disabled={exec.isPending} onClick={() => exec.mutate()}>
                Registrar atendimento
              </button>
            )}
            {active && started && (
              <button className="btn" disabled={status.isPending} onClick={() => outcome('nao_compareceu', 'Observação (opcional):')}>
                Cliente não compareceu
              </button>
            )}
            {active && (
              <button className="btn btn-danger" disabled={status.isPending} onClick={() => outcome('cancelado', 'Motivo do cancelamento (opcional):')}>
                Cancelar compromisso
              </button>
            )}
            {!active && a.status !== 'realizado' && <span className="small muted">Compromisso encerrado; o histórico é preservado.</span>}
          </div>
        </section>
      </div>
      {a.clientId && !a.private && a.typeKind !== 'personal' && <AppointmentMessages appointment={a} />}
      {reschedule && <RescheduleDialog appointment={a} onClose={() => setReschedule(false)} onDone={(nid) => navigate(`/agenda/${nid}`)} />}
    </>
  );
}

function RescheduleDialog({ appointment, onClose, onDone }: { appointment: Appointment; onClose: () => void; onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const s = localParts(appointment.startsAt);
  const [day, setDay] = useState(s.date);
  const [start, setStart] = useState(s.time);
  const [end, setEnd] = useState(localParts(appointment.endsAt).time);
  const [conflicts, setConflicts] = useState<ConflictInfo[] | null>(null);
  const m = useMutation({
    mutationFn: (confirmConflicts: boolean) =>
      api.post<{ id: string }>(`/appointments/${appointment.id}/reschedule`, {
        startsAt: zonedIso(day, start),
        endsAt: zonedIso(end <= start ? addDays(day, 1) : day, end),
        version: appointment.version,
        confirmConflicts,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      void qc.invalidateQueries({ queryKey: ['appointment', appointment.id] });
      onDone(r.id);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'schedule_conflict') setConflicts((e.details as { conflicts: ConflictInfo[] }).conflicts);
    },
  });
  const err = m.error instanceof ApiError && m.error.code === 'schedule_conflict' ? null : m.error;
  return (
    <Dialog title="Reagendar" onClose={onClose}>
      <p className="small muted">O compromisso atual fica registrado como “reagendado”, ligado à nova marcação.</p>
      <ErrorAlert error={err} />
      {conflicts && (
        <div className="alert alert-warning">
          <strong>Sobreposição de horário</strong>
          {conflicts.map((c) => `${timeOf(c.startsAt)}–${timeOf(c.endsAt)} ${c.title}`).join('; ')}
          <div className="actions" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-sm" onClick={() => m.mutate(true)}>
              Reagendar mesmo assim
            </button>
          </div>
        </div>
      )}
      <div className="form">
        <Field label="Nova data">{(p) => <input {...p} type="date" value={day} onChange={(e) => setDay(e.target.value)} />}</Field>
        <div className="form-row">
          <Field label="Início">{(p) => <input {...p} type="time" value={start} onChange={(e) => setStart(e.target.value)} />}</Field>
          <Field label="Fim">{(p) => <input {...p} type="time" value={end} onChange={(e) => setEnd(e.target.value)} />}</Field>
        </div>
        <div className="actions">
          <button
            className="btn btn-primary"
            disabled={m.isPending}
            onClick={() => {
              setConflicts(null);
              m.mutate(false);
            }}
          >
            Reagendar
          </button>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function AppointmentMessages({ appointment: a }: { appointment: Appointment }) {
  const active = a.status === 'agendado' || a.status === 'confirmado';
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  return (
    <section className="card">
      <h2>Mensagens ao cliente</h2>
      <p className="small muted">Salvar ou alterar a agenda nunca envia mensagem. Prepare, confira a prévia e confirme o envio manualmente.</p>
      <div className="segmented" role="group" aria-label="Canal" style={{ marginBottom: '0.5rem' }}>
        <button aria-pressed={channel === 'email'} onClick={() => setChannel('email')}>
          E-mail
        </button>
        <button aria-pressed={channel === 'whatsapp'} onClick={() => setChannel('whatsapp')}>
          WhatsApp
        </button>
      </div>
      <div className="actions">
        {active && !a.rescheduledFromId && <PrepareMessageButton label="Preparar confirmação" body={{ purpose: 'confirmacao_agenda', channel, appointmentId: a.id }} />}
        {active && a.rescheduledFromId && <PrepareMessageButton label="Preparar aviso de reagendamento" body={{ purpose: 'reagendamento', channel, appointmentId: a.id }} />}
        {active && <PrepareMessageButton label="Preparar lembrete" body={{ purpose: 'lembrete', channel, appointmentId: a.id }} />}
        {a.status === 'cancelado' && <PrepareMessageButton label="Preparar aviso de cancelamento" body={{ purpose: 'cancelamento', channel, appointmentId: a.id }} />}
      </div>
      <RelatedMessages filter={{ appointmentId: a.id }} />
    </section>
  );
}
